// src/pages/Shop/VoucherTermsPage.js
//
// Public legal page for "Regulamin bonów / voucherów podarunkowych online"
// that lives inside the React mini-shop.
//
// GOALS
// -----
// - Pure React page (no WordPress dependency).
// - Visual style consistent with ShopPage & PrivacyPolicyPage (white card on
//   grey background, logo pill, typography).
// - All user-facing legal copy now lives in i18n dictionaries. The component
//   stays responsible only for layout, public-language switching and safe link
//   rendering.
//
// ROUTING
// -------
// App.js (PublicLayout) should expose this component as:
//
//   import VoucherTermsPage from "./pages/Shop/VoucherTermsPage";
//
//   <Route path="/regulamin" element={<VoucherTermsPage />} />
//
// With Router basename="/app" the final URL is:
//   https://hb.konin.pl/app/regulamin
//
// PUBLIC LANGUAGE POLICY
// ----------------------
// This is an anonymous public page. It offers only real display languages:
// Polish and English. The login-only "Profile language" option is intentionally
// not available here because there is no authenticated user profile on public
// legal pages.
//
// SCROLL FIX
// ----------
// We force the outer wrapper to be scrollable to avoid any overflow issues on
// some layouts.
//
// LEGAL-COPY NOTE
// ---------------
// The Polish copy is the business source text that was originally delivered for
// the public voucher shop. The English copy is an application translation and
// should be legally reviewed before being treated as a binding public legal
// translation.

import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import PublicLanguageDropdown from "../../components/PublicLanguageDropdown";
import { normalizeLanguage } from "../../i18n";
import "./ShopPage.css";

const PUBLIC_LANGUAGE_QUERY_PARAM = "lng";

function readPublicLanguageFromUrl() {
  if (typeof window === "undefined") return null;

  const raw = new URLSearchParams(window.location.search).get(
    PUBLIC_LANGUAGE_QUERY_PARAM
  );

  if (!raw) return null;

  const language = String(raw).trim().toLowerCase().split("-")[0];
  return language === "pl" || language === "en" ? language : null;
}

function buildPublicUrlWithLanguage(path, language) {
  const normalizedLanguage = normalizeLanguage(language);
  const separator = path.includes("?") ? "&" : "?";
  return `${path}${separator}${PUBLIC_LANGUAGE_QUERY_PARAM}=${encodeURIComponent(
    normalizedLanguage
  )}`;
}

function replaceCurrentUrlPublicLanguage(language) {
  if (typeof window === "undefined") return;

  const nextUrl = new URL(window.location.href);
  nextUrl.searchParams.set(
    PUBLIC_LANGUAGE_QUERY_PARAM,
    normalizeLanguage(language)
  );

  window.history.replaceState({}, "", nextUrl.toString());
}
 // reuse same styles as ShopPage


function renderLinkedText(text) {
  const value = String(text || "");
  const parts = value.split(/(https?:\/\/hb\.konin\.pl\/shop|https?:\/\/hb\.konin\.pl|hb\.konin\.pl|kontakt@hb\.konin\.pl)/g);

  return parts.map((part, index) => {
    if (!part) return null;

    const href = part.startsWith("http")
      ? part
      : part.includes("@")
      ? `mailto:${part}`
      : `https://${part}`;

    const isLink =
      part.startsWith("http") ||
      part === "hb.konin.pl" ||
      part.includes("@hb.konin.pl");

    if (!isLink) {
      return <React.Fragment key={`text-${index}`}>{part}</React.Fragment>;
    }

    return (
      <a key={`link-${index}`} href={href} target="_blank" rel="noopener noreferrer">
        {part}
      </a>
    );
  });
}

function renderMultilineText(text, keyPrefix) {
  return String(text || "")
    .split("\n")
    .map((line, index, lines) => (
      <React.Fragment key={`${keyPrefix}-${index}`}>
        {renderLinkedText(line)}
        {index < lines.length - 1 ? <br /> : null}
      </React.Fragment>
    ));
}

function renderLegalBlock(block, sectionId, blockIndex) {
  if (!block) return null;

  if (block.type === "list") {
    return (
      <ul className="shop-legal-list" key={`${sectionId}-list-${blockIndex}`}>
        {(block.items || []).map((item, itemIndex) => (
          <li key={`${sectionId}-item-${blockIndex}-${itemIndex}`}>
            {renderMultilineText(item, `${sectionId}-item-${blockIndex}-${itemIndex}`)}
          </li>
        ))}
      </ul>
    );
  }

  return (
    <p className="shop-legal-text" key={`${sectionId}-paragraph-${blockIndex}`}>
      {renderMultilineText(block.text, `${sectionId}-paragraph-${blockIndex}`)}
    </p>
  );
}

function renderLegalSections(sections) {
  if (!Array.isArray(sections)) return null;

  return sections.map((section, sectionIndex) => {
    const sectionKey = section.id || `section-${sectionIndex}`;
    return (
      <section className="shop-legal-section" key={sectionKey}>
        <h2 className="shop-legal-section-title">{section.title}</h2>
        {(section.blocks || []).map((block, blockIndex) =>
          renderLegalBlock(block, sectionKey, blockIndex)
        )}
      </section>
    );
  });
}


const VoucherTermsPage = () => {
  const { i18n } = useTranslation("shop");
  const salonName = "HB Health & Beauty";

  // PUBLIC_URL is injected at build time by CRA and reflects the "homepage"
  // setting (e.g. "/app" in production). Keep it aligned with the other public
  // shop pages so static logo loading works under BrowserRouter basename.
  const appBasePath = process.env.PUBLIC_URL || "/app";
  const logoSrc = `${appBasePath}/hb_logo.png`;

  // Link back to the shop page.
  //
  // With basename "/app" and route "/shop", the full URL is:
  //   https://hb.konin.pl/app/shop
  const [urlLanguageOverride, setUrlLanguageOverride] = useState(() =>
    readPublicLanguageFromUrl()
  );

  const currentPublicLanguage = normalizeLanguage(
    urlLanguageOverride || i18n.resolvedLanguage || i18n.language
  );

  // Important: use a fixed translator for the language resolved from the URL.
  // React-i18next normally re-renders after changeLanguage(), but legal pages
  // are often opened directly in a new tab. Using getFixedT() makes the first
  // render deterministic for /regulamin?lng=en and
  // /polityka-prywatnosci?lng=en even before the global i18n state finishes
  // updating.
  const shopT = i18n.getFixedT(currentPublicLanguage, "shop");

  const shopPath = `${appBasePath}/shop`;
  const shopUrl = buildPublicUrlWithLanguage(
    typeof window !== "undefined"
      ? `${window.location.origin}${shopPath}`
      : shopPath,
    currentPublicLanguage
  );

  useEffect(() => {
    // Legal pages are frequently opened from ShopPage in a new browser tab.
    // We intentionally do not use browser storage for anonymous language
    // selection, so the shop passes the current public language through the
    // URL. The page then applies it once after mount.
    const urlLanguage = readPublicLanguageFromUrl();
    if (!urlLanguage) return;

    setUrlLanguageOverride(urlLanguage);

    if (normalizeLanguage(i18n.resolvedLanguage || i18n.language) !== urlLanguage) {
      i18n.changeLanguage(urlLanguage);
    }
  }, [i18n]);

  const handlePublicLanguageChange = (selection) => {
    if (!selection?.language) return;

    const nextLanguage = normalizeLanguage(selection.language);
    setUrlLanguageOverride(nextLanguage);
    replaceCurrentUrlPublicLanguage(nextLanguage);
    i18n.changeLanguage(nextLanguage);
  };

  const sections = shopT("legalPages.voucherTerms.sections", { returnObjects: true });

  return (
    // Force full viewport height and vertical scroll.
    <div className="shop-legal-page" style={{ minHeight: "100vh", overflowY: "auto" }}>
      <div className="shop-legal-container">
        {/* Top bar with logo, language dropdown and back link. */}
        <header className="shop-legal-header">
          <div className="shop-public-language-row">
            <PublicLanguageDropdown
              value={currentPublicLanguage}
              onChange={handlePublicLanguageChange}
              className="shop-public-language-dropdown"
              style={{ marginBottom: 0 }}
            />
          </div>

          <div className="shop-legal-header-main">
            <div className="shop-logo-pill">
              <img src={logoSrc} alt={salonName} className="shop-logo-img" />
            </div>
            <div>
              <h1 className="shop-legal-title">
                {shopT("legalPages.voucherTerms.title")}
              </h1>
              <p className="shop-legal-subtitle-text">
                {shopT("legalPages.voucherTerms.subtitle")}
              </p>
            </div>
          </div>

          <a href={shopUrl} className="shop-legal-back-link">
            {shopT("legalPages.voucherTerms.backToShop")}
          </a>
        </header>

        {/* Main white card with the full terms text. */}
        <main className="shop-legal-card">{renderLegalSections(sections)}</main>
      </div>
    </div>
  );
};

export default VoucherTermsPage;
